import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { searchSkills } from '../../../../api/Employee/skills';
import { SkillView } from '../../../../models/employee';
import Icon from '../../../../components/AppIcon';

interface SkillAutocompleteProps {
  value: string;
  onChange: (value: string, skillId?: number) => void;
  // Omit to hide the create affordance. Returns the new skill, or null if creation failed.
  onCreate?: (name: string) => Promise<SkillView | null>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

const SkillAutocomplete: React.FC<SkillAutocompleteProps> = ({
  value,
  onChange,
  onCreate,
  label,
  placeholder,
  required = false,
  error
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<SkillView[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isCreating, setIsCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchForSkills = async (term: string) => {
    if (term.length < 2) {
      setSuggestions([]);
      // Still open it if there is text to create from: the panel below renders only when there is
      // either a suggestion or a create row, so this cannot show an empty box.
      setIsOpen(term.trim().length > 0);
      return;
    }

    setIsLoading(true);
    try {
      const result = await searchSkills(term);
      if (result.status === 200 && result.payload) {
        setSuggestions(result.payload);
        // Was `setIsOpen(result.payload.length > 0)`, which CLOSED the panel whenever the search
        // matched nothing — the exact case the create row exists for. A brand-new skill name matches
        // nothing by definition, so "Add <name>" lived inside a list that never opened, and the only
        // way to create was an Enter key nothing advertises. The render gate below still requires a
        // suggestion or a create row, so opening unconditionally shows no empty panel.
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Failed to search skills:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue, undefined); // Clear skillId when typing
    setSelectedIndex(-1);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchForSkills(newValue);
    }, 300);
  };

  const handleSelectSuggestion = (skill: SkillView) => {
    setInputValue(skill.name);
    onChange(skill.name, skill.id);
    setIsOpen(false);
    setSuggestions([]);
  };

  const exactMatch = suggestions.some(s => s.name.toLowerCase() === inputValue.trim().toLowerCase());
  const canCreate = !!onCreate && inputValue.trim().length > 0 && !exactMatch;

  const handleCreate = async () => {
    if (!onCreate || isCreating) return;
    setIsCreating(true);
    try {
      const created = await onCreate(inputValue.trim());
      if (created) {
        handleSelectSuggestion(created);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        if (!isOpen || suggestions.length === 0) return;
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        if (!isOpen || suggestions.length === 0) return;
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        } else if (canCreate) {
          void handleCreate();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative space-y-2">
      {label && (
        <label className="text-sm font-medium leading-none text-foreground">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0 || canCreate) setIsOpen(true); }}
          placeholder={placeholder || t('skillAutocomplete.placeholder')}
          className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
            error ? 'border-destructive' : 'border-input'
          }`}
          required={required}
        />
        {isLoading && (
          <div className="absolute end-3 top-1/2 -translate-y-1/2">
            <Icon name="Loader2" size={16} className="animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && (suggestions.length > 0 || canCreate) && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.length > 0 && (
            <div className="p-2 text-xs text-muted-foreground border-b border-border">
              {t('skillAutocomplete.existingMatches')}
            </div>
          )}
          {suggestions.map((skill, index) => (
            <button
              key={skill.id}
              type="button"
              onClick={() => handleSelectSuggestion(skill)}
              className={`w-full text-start px-3 py-2 text-sm hover:bg-muted flex items-center justify-between ${
                index === selectedIndex ? 'bg-muted' : ''
              }`}
            >
              <span className="font-medium text-foreground">{skill.name}</span>
              {skill.description && (
                <span className="text-xs text-muted-foreground truncate ms-2 max-w-[150px]">
                  {skill.description}
                </span>
              )}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={isCreating}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-start text-sm font-medium text-primary hover:bg-muted disabled:opacity-60"
              data-testid="skill-autocomplete-create"
            >
              {isCreating
                ? <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
                : <Icon name="Plus" size={14} aria-hidden="true" />}
              {t('skillAutocomplete.createOption', { name: inputValue.trim() })}
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};

export default SkillAutocomplete;
